import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import type { PublicShareItemSummary, PublicShareLink } from "@/types";
import {
  BUILDING_STATUS_LABEL_BY_LANG,
  CONDITION_LABEL_BY_LANG,
  DEAL_TYPE_LABEL_BY_LANG,
  DEFAULT_LANG,
  INTL_LOCALE_BY_LANG,
  LANG_LABELS,
  PROPERTY_TYPE_LABEL_BY_LANG,
  SUPPORTED_LANGS,
  type ShareLang,
  dealTypeLabel,
  listingHeadline,
  meridianFontVars,
  normalizeLang,
  t,
} from "@/i18n/share";

/** Per-share localStorage keys — let a buyer's manual lang/currency
 *  choice persist across reloads without leaking between links. */
const langStorageKey = (token: string) => `agencygrid:share-lang:${token}`;
const currencyStorageKey = (token: string) =>
  `agencygrid:share-currency:${token}`;

/** Currency display mode for the public page. ``native`` shows each
 *  listing's original currency (USD/GEL/EUR). ``usd`` shows ``price_usd``
 *  when populated, falling back to native on listings without an FX
 *  value. The mode lives in localStorage; default is ``native``. */
export type DisplayCurrency = "native" | "usd";

export function normalizeCurrency(value: string | null | undefined): DisplayCurrency {
  return value === "usd" ? "usd" : "native";
}

/** Pick the price + currency to render given the active mode. Falls
 *  back to native when ``usd`` is chosen but the listing has no
 *  ``price_usd`` value (e.g. placements). */
export function pickDisplayPrice(
  item: { price: string | null; currency: string | null; price_usd?: string | null },
  mode: DisplayCurrency,
): { value: string | null; currency: string | null } {
  if (mode === "usd" && item.price_usd) return { value: item.price_usd, currency: "USD" };
  return { value: item.price, currency: item.currency };
}

/** Curated buyer page — one hero offer at a time + side rail of alternatives.
 *
 *  The page is designed like a printed brochure: the hero image dominates
 *  the fold, the price + headline facts overlay the bottom of the image,
 *  the agent contact card is always one glance away, and the alternative
 *  offers sit on the right (or beneath, on mobile) so the buyer can flip
 *  through them in seconds.
 *
 *  Language: the agent picks a default at link-creation time (defaults to
 *  Georgian). The buyer can override via the flag picker in the top bar;
 *  the choice persists per-token in localStorage so they don't see the
 *  wrong language on the next visit.
 */
export function ClientShareView() {
  const { token } = useParams<{ token: string }>();
  const [share, setShare] = useState<PublicShareLink | null>(null);
  const [error, setError] = useState<{ kind: "missing" | "gone" | "other"; message: string } | null>(
    null,
  );
  const [langOverride, setLangOverride] = useState<ShareLang | null>(() => {
    if (!token || typeof window === "undefined") return null;
    const v = window.localStorage.getItem(langStorageKey(token));
    return v ? normalizeLang(v) : null;
  });
  const [currency, setCurrencyState] = useState<DisplayCurrency>(() => {
    if (!token || typeof window === "undefined") return "native";
    return normalizeCurrency(window.localStorage.getItem(currencyStorageKey(token)));
  });

  const lang: ShareLang = langOverride ?? normalizeLang(share?.language ?? null);

  const setLang = useCallback(
    (next: ShareLang) => {
      setLangOverride(next);
      if (token && typeof window !== "undefined") {
        window.localStorage.setItem(langStorageKey(token), next);
      }
    },
    [token],
  );

  const setCurrency = useCallback(
    (next: DisplayCurrency) => {
      setCurrencyState(next);
      if (token && typeof window !== "undefined") {
        window.localStorage.setItem(currencyStorageKey(token), next);
      }
    },
    [token],
  );

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await api<PublicShareLink>(`/api/public/share/${token}`);
      setShare(r);
      setError(null);
    } catch (err) {
      const fallbackLang = langOverride ?? DEFAULT_LANG;
      if (err instanceof ApiError) {
        if (err.status === 404)
          setError({ kind: "missing", message: t("err_share_missing", fallbackLang) });
        else if (err.status === 410)
          setError({ kind: "gone", message: t("err_share_gone", fallbackLang) });
        else setError({ kind: "other", message: err.message });
      } else {
        setError({
          kind: "other",
          message: err instanceof Error ? err.message : t("err_failed_load", fallbackLang),
        });
      }
    }
  }, [token, langOverride]);

  useEffect(() => {
    load();
  }, [load]);

  // Light polling while preparing — once ready/failed the loop stops.
  useEffect(() => {
    if (!share || share.status !== "preparing") return;
    const id = window.setInterval(load, 3000);
    return () => window.clearInterval(id);
  }, [share, load]);

  const readyItems = useMemo(
    () => (share?.items ?? []).filter((i) => i.status === "ready"),
    [share],
  );

  if (error) {
    return (
      <PublicShell title={t("err_link_unavailable", lang)}>{error.message}</PublicShell>
    );
  }
  if (!share) {
    return (
      <PublicShell title={t("loading_title", lang)}>
        {t("loading_grid_blurb", lang)}
      </PublicShell>
    );
  }

  const stillPreparing = share.items.length - readyItems.length;

  return (
    <div
      className="mshare min-h-screen bg-[var(--mrd-bg)] text-[var(--mrd-ink)]"
      data-lang={lang}
      style={meridianFontVars(lang)}
    >
      <TopBar
        agent={share.agent}
        lang={lang}
        onLangChange={setLang}
        currency={currency}
        onCurrencyChange={setCurrency}
      />

      <main className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-28 sm:pb-20 space-y-10 sm:space-y-12">
        <Intro share={share} lang={lang} />

        {share.status === "preparing" && stillPreparing > 0 && (
          <div className="rounded-2xl border border-[var(--mrd-gold)]/30 bg-[var(--mrd-gold)]/[0.08] text-[var(--mrd-gold)] px-4 py-3 text-sm flex items-center gap-3">
            <span className="inline-block w-2 h-2 rounded-full bg-[var(--mrd-gold)] animate-pulse" />
            {t("preparing_more", lang, { count: stillPreparing })}
          </div>
        )}

        {share.status === "failed" && readyItems.length === 0 && (
          <div className="rounded-2xl bg-white border border-[var(--mrd-line)] p-6 text-[var(--mrd-muted)] shadow-[0_2px_24px_rgba(18,32,54,.05)]">
            {t("failed_no_offers", lang)}
          </div>
        )}

        {/* One uniform editorial grid — every matched listing gets equal
            weight (no featured hero, no side rail). The whole card links to
            its detail page. */}
        {readyItems.length > 0 && (
          <section className="space-y-5">
            <div className="flex items-end justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="h-px w-7 bg-[var(--mrd-accent)]" />
                <span className="font-mono text-[11px] uppercase tracking-[.28em] text-[var(--mrd-muted)]">
                  {t("all_properties", lang)}
                </span>
              </div>
              <span className="font-mono text-[11px] text-[var(--mrd-muted)] tabular-nums shrink-0">
                {t("total_count", lang, { count: readyItems.length })}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {readyItems.map((item) => (
                <GridCard
                  key={item.id}
                  token={share.token}
                  item={item}
                  lang={lang}
                  currency={currency}
                />
              ))}
            </div>
          </section>
        )}

        {/* Pre-footer CTA. Funnels the "I scrolled to the end, now what?"
            buyer into the agent's contact path without making them scroll
            back up to the sticky top bar. */}
        {share.agent.phone && (
          <PreFooterCTA agent={share.agent} lang={lang} />
        )}
      </main>

      <ShareFooter share={share} lang={lang} />

      {/* Sticky mobile CTA. Only renders on phones — the top bar already
          has a phone button on tablet+. Sits above the iOS home indicator
          via the share-mobile-cta-pb safe-area helper. */}
      {share.agent.phone && (
        <MobileStickyCTA agent={share.agent} lang={lang} />
      )}
    </div>
  );
}

function PreFooterCTA({
  agent,
  lang,
}: {
  agent: PublicShareLink["agent"];
  lang: ShareLang;
}) {
  return (
    <section className="rounded-2xl bg-[var(--mrd-navy)] text-white px-6 py-14 sm:px-12 sm:py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        <div className="min-w-0 max-w-xl">
          <div className="mb-4 flex items-center gap-3">
            <span className="h-px w-7 bg-[var(--mrd-accent)]" />
            <span className="font-mono text-[11px] uppercase tracking-[.28em] text-white/50">
              {t("footer_contact_label", lang)}
            </span>
          </div>
          <h3 className="mshare-serif text-[clamp(28px,4vw,44px)] font-semibold text-white leading-[1.05]">
            {t("cta_ready_title", lang)}
          </h3>
          <p className="text-sm sm:text-base text-white/65 mt-3 leading-relaxed">
            {t("cta_ready_blurb", lang)}
          </p>
        </div>
        <div className="flex flex-col xs:flex-row gap-3 md:flex-shrink-0">
          {agent.phone && (
            <a
              href={`tel:${agent.phone}`}
              className="inline-flex items-center justify-center gap-2 rounded-[999px] bg-[var(--mrd-accent)] hover:bg-[var(--mrd-accent-hover)] text-[var(--mrd-ink)] text-[15px] font-semibold px-7 py-4 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
              </svg>
              {t("btn_call_agent", lang)}
            </a>
          )}
          {agent.phone && (
            <a
              href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(t("whatsapp_default_msg", lang))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-[999px] border border-white/25 bg-white/10 hover:bg-white/20 text-white text-[15px] font-semibold px-7 py-4 transition-colors"
            >
              <WhatsAppIcon className="w-4 h-4" />
              {t("btn_whatsapp", lang)}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

/** Real Meridian navy footer — brand column (logo + wordmark) + a curated
 *  column (selection date / expiry) + an agent-contact column. Mirrors the
 *  meridianagency.ge Footer structure/typography. */
function ShareFooter({
  share,
  lang,
}: {
  share: PublicShareLink;
  lang: ShareLang;
}) {
  const agent = share.agent;
  const fmt = (style: "long" | "medium") =>
    new Date(share.expires_at).toLocaleDateString(INTL_LOCALE_BY_LANG[lang], {
      dateStyle: style,
    });
  return (
    <footer className="bg-[var(--mrd-navy)] text-white">
      <div className="mx-auto max-w-[1320px] px-6 py-16 sm:py-20 md:px-8">
        <div className="grid gap-10 md:grid-cols-[1.6fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-3.5">
              <img
                src="/logos/meridian-logo.png"
                alt="Meridian Tbilisi"
                width={54}
                height={54}
                className="rounded-xl object-cover"
              />
              <div>
                <div className="mshare-serif text-xl font-semibold leading-none">
                  Meridian
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[.22em] text-white/60 mt-1">
                  Tbilisi
                </div>
              </div>
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/65">
              {t("footer_tagline", lang)}
            </p>
          </div>

          <div>
            <div className="font-mono text-[11px] uppercase tracking-[.14em] text-white/50">
              {t("footer_selection_label", lang)}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/80">
              {t("footer_curated", lang, { date: fmt("long"), expires: fmt("medium") })}
            </p>
          </div>

          <div>
            <div className="font-mono text-[11px] uppercase tracking-[.14em] text-white/50">
              {t("footer_contact_label", lang)}
            </div>
            <div className="mt-4 flex flex-col gap-3 text-sm text-white/80">
              {agent.phone && (
                <a href={`tel:${agent.phone}`} className="hover:text-white">
                  {agent.phone}
                </a>
              )}
              {agent.email && (
                <a href={`mailto:${agent.email}`} className="hover:text-white">
                  {agent.email}
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-2 border-t border-white/10 pt-6 text-xs text-white/45 md:flex-row md:items-center md:justify-between">
          <span>
            © {new Date(share.expires_at).getFullYear()} Meridian Tbilisi
          </span>
          <span>Meridian · Tbilisi</span>
        </div>
      </div>
    </footer>
  );
}

function MobileStickyCTA({
  agent,
  lang,
}: {
  agent: PublicShareLink["agent"];
  lang: ShareLang;
}) {
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[var(--mrd-line)] bg-[rgba(255,255,255,.95)] backdrop-blur-md share-mobile-cta-pb pt-3 px-3 safe-px">
      <div className="flex gap-2">
        {agent.phone && (
          <a
            href={`tel:${agent.phone}`}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-[999px] bg-[var(--mrd-accent)] hover:bg-[var(--mrd-accent-hover)] active:scale-[0.98] text-[var(--mrd-ink)] text-sm font-semibold py-3.5 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
            </svg>
            {t("btn_call", lang)}
          </a>
        )}
        {agent.phone && (
          <a
            href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(t("whatsapp_default_msg", lang))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-[999px] border border-[rgba(16,120,80,.3)] bg-[rgba(16,120,80,.08)] active:scale-[0.98] text-[#0f7a50] text-sm font-semibold py-3.5 transition-colors"
          >
            <WhatsAppIcon className="w-3.5 h-3.5" />
            {t("btn_whatsapp", lang)}
          </a>
        )}
      </div>
    </div>
  );
}

// ─── shell ──────────────────────────────────────────────────────────────────

function TopBar({
  agent,
  lang,
  onLangChange,
  currency,
  onCurrencyChange,
}: {
  agent: PublicShareLink["agent"];
  lang: ShareLang;
  onLangChange: (l: ShareLang) => void;
  currency: DisplayCurrency;
  onCurrencyChange: (c: DisplayCurrency) => void;
}) {
  return (
    <header className="sticky top-0 z-40 border-b border-[var(--mrd-line)] bg-[rgba(255,255,255,.92)] backdrop-blur-[14px] shadow-[0_1px_16px_rgba(18,32,54,.06)] safe-pt">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 md:px-8 h-[68px] flex items-center justify-between gap-2 sm:gap-3">
        {/* Left: the Meridian wordmark (matches the marketing site's NavBar). */}
        <div className="flex items-center min-w-0">
          <span className="flex items-baseline flex-shrink-0 text-[var(--mrd-navy)]">
            <span className="mshare-serif text-2xl leading-none font-semibold">
              Meridian
            </span>
            <span className="font-mono ml-2 text-[10px] uppercase tracking-[.26em] opacity-70">
              Tbilisi
            </span>
          </span>
        </div>

        {/* Right: viewer controls. Language and currency are tucked into
            one segmented-control cluster so they read as a single concern.
            The phone CTA stays as a coloured pill — primary action of the
            whole page. WhatsApp falls off below sm to keep room. */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          <CurrencySwitcher currency={currency} onChange={onCurrencyChange} lang={lang} />
          <LangSwitcher lang={lang} onChange={onLangChange} />
          {agent.phone && (
            <a
              href={`tel:${agent.phone}`}
              className="inline-flex items-center gap-1.5 rounded-[999px] bg-[var(--mrd-accent)] hover:bg-[var(--mrd-accent-hover)] text-[var(--mrd-ink)] text-xs font-semibold px-4 py-2 transition-colors"
              aria-label={t("btn_call", lang)}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
              </svg>
              {/* Show full phone on lg+; "Call" label sm-md; just icon on phones. */}
              <span className="hidden lg:inline">{agent.phone}</span>
              <span className="hidden sm:inline lg:hidden">{t("btn_call", lang)}</span>
            </a>
          )}
          {agent.phone && (
            <a
              href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(t("whatsapp_default_msg", lang))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-[999px] border border-[rgba(16,120,80,.3)] bg-[rgba(16,120,80,.08)] hover:bg-[rgba(16,120,80,.16)] text-[#0f7a50] text-xs font-semibold px-4 py-2 transition-colors"
              title={t("btn_whatsapp", lang)}
            >
              <WhatsAppIcon className="w-3 h-3" />
              <span className="hidden md:inline">{t("btn_whatsapp", lang)}</span>
            </a>
          )}
        </div>
      </div>
    </header>
  );
}

export function LangSwitcher({
  lang,
  onChange,
}: {
  lang: ShareLang;
  onChange: (l: ShareLang) => void;
}) {
  // Pill-shaped segmented control. Compact enough to live in the top bar
  // alongside the call/WhatsApp buttons. Uses flag emojis for instant
  // recognition; the per-language ``native`` label is the tooltip + an
  // sr-only string.
  return (
    <div
      role="radiogroup"
      aria-label={t("language_picker_label", lang)}
      className="inline-flex items-center gap-1"
    >
      {SUPPORTED_LANGS.map((code) => {
        const meta = LANG_LABELS[code];
        const active = code === lang;
        return (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={active}
            title={meta.native}
            onClick={() => onChange(code)}
            className={`font-mono rounded-[999px] px-2.5 py-1.5 text-[10px] uppercase tracking-[.05em] leading-none transition-colors ${
              active
                ? "bg-[var(--mrd-accent)] text-[var(--mrd-ink)] font-semibold"
                : "text-[var(--mrd-navy)] opacity-70 hover:opacity-100"
            }`}
          >
            <span aria-hidden>{meta.flag}</span>
            <span className="ml-1 hidden md:inline">{code.toUpperCase()}</span>
            <span className="sr-only">{meta.native}</span>
          </button>
        );
      })}
    </div>
  );
}

function CurrencySwitcher({
  currency,
  onChange,
  lang,
}: {
  currency: DisplayCurrency;
  onChange: (next: DisplayCurrency) => void;
  lang: ShareLang;
}) {
  // Two-pill segmented control: ``Original`` (each listing in its own
  // currency) ↔ ``USD``. Mirrors LangSwitcher's visual language so they
  // look like one control group when stacked. Hidden when the share has
  // no listings carrying a USD price (no FX value to switch to).
  return (
    <div
      role="radiogroup"
      aria-label={t("currency_picker_label", lang)}
      className="inline-flex items-center gap-1"
    >
      {([
        { code: "native" as const, label: t("currency_native", lang), short: "₾" },
        { code: "usd" as const, label: t("currency_usd", lang), short: "$" },
      ]).map(({ code, label, short }) => {
        const active = code === currency;
        return (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={active}
            title={label}
            onClick={() => onChange(code)}
            className={`font-mono rounded-[999px] px-2.5 py-1.5 text-[10px] uppercase tracking-[.05em] leading-none transition-colors ${
              active
                ? "bg-[var(--mrd-accent)] text-[var(--mrd-ink)] font-semibold"
                : "text-[var(--mrd-navy)] opacity-70 hover:opacity-100"
            }`}
          >
            <span className="md:hidden" aria-hidden>{short}</span>
            <span className="hidden md:inline">{label}</span>
            <span className="sr-only">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

function Intro({ share, lang }: { share: PublicShareLink; lang: ShareLang }) {
  const count = share.items.filter((i) => i.status === "ready").length;
  const clientName = share.client_first_name?.trim() || null;

  // Headline. We deliberately pass NO params to t() — both ``{count}``
  // and ``{name}`` are emitted as literal placeholders so the JSX-level
  // renderer below can wrap each in its own accent-coloured span instead
  // of pre-substituting them as plain text.
  const headlineTemplate = (() => {
    if (count > 1) {
      return clientName
        ? t("intro_headline_many_with_name", lang)
        : t("intro_headline_many_count_html", lang);
    }
    return clientName
      ? t("intro_headline_one_with_name", lang)
      : t("intro_headline_one", lang);
  })();

  // ``{count}`` lands in pre-substituted form for the count > 1 branches
  // (we passed it to t()); ``{name}`` was preserved as a literal so we
  // can wrap it in its own span. Build a single tokenised render path
  // so the markup logic stays simple and testable.
  const renderedHeadline = renderHeadline(headlineTemplate, {
    count: count > 1 ? String(count) : null,
    name: clientName,
  });

  return (
    <div className="text-center max-w-3xl mx-auto pt-6 sm:pt-10 pb-2 px-1">
      <div className="mb-5 flex items-center justify-center gap-3">
        <span className="h-px w-7 bg-[var(--mrd-accent)]" />
        <span className="font-mono text-[11px] uppercase tracking-[.28em] text-[var(--mrd-muted)]">
          {t("pill_handpicked", lang)}
        </span>
        <span className="h-px w-7 bg-[var(--mrd-accent)]" />
      </div>
      <h1 className="mshare-serif font-medium mt-1 text-[clamp(38px,7vw,80px)] leading-[1.05] tracking-[-0.01em] text-[var(--mrd-navy)] text-balance">
        {renderedHeadline}
      </h1>
      <p className="text-[var(--mrd-muted)] mt-5 text-base sm:text-lg leading-relaxed">
        {t("intro_blurb_no_name", lang)}
      </p>
    </div>
  );
}

/** Tokenise a translated headline that may contain {count} (already
 *  substituted but we want to colour-highlight) and/or {name} (still a
 *  literal placeholder). Returns a JSX fragment with the right spans
 *  in the right order regardless of locale word-order. */
function renderHeadline(
  template: string,
  vals: { count: string | null; name: string | null },
): React.ReactNode {
  // Split on either placeholder; preserve order; emit spans for the
  // matched tokens, plain text for the rest.
  const parts: React.ReactNode[] = [];
  const re = /(\{count\}|\{name\})/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(template)) !== null) {
    if (m.index > last) parts.push(template.slice(last, m.index));
    if (m[0] === "{count}" && vals.count) {
      parts.push(
        <span key={`c-${key++}`} className="text-[var(--mrd-gold)]">
          {vals.count}
        </span>,
      );
    } else if (m[0] === "{name}" && vals.name) {
      parts.push(
        <span key={`n-${key++}`} className="text-[var(--mrd-gold)]">
          {vals.name}
        </span>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < template.length) parts.push(template.slice(last));
  return <>{parts}</>;
}

// ─── below-the-fold full grid ──────────────────────────────────────────────

/** Staff-only chip linking out to the portal listing a card was scraped
 *  from. The API withholds ``source_url`` from anonymous buyers, so simply
 *  rendering when it is present is the whole access rule — there is no
 *  client-side gate to get wrong. Deliberately styled as an internal
 *  annotation (mono, navy) rather than in the brochure's own visual
 *  language, so nobody mistakes it for something the client is meant to see.
 */
export function SourceLinkBadge({
  url,
  className = "",
}: {
  url: string;
  className?: string;
}) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={`Staff only — open the source listing:\n${url}`}
      // Cards wrap their body in a <Link>; this must never sit inside it
      // (nested anchors are invalid), hence the sibling-overlay placement.
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 rounded-full bg-[var(--mrd-navy)]/85 backdrop-blur-md px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[.14em] text-white/90 hover:bg-[var(--mrd-navy)] hover:text-white transition-colors ${className}`}
    >
      Source ↗
    </a>
  );
}

function GridCard({
  token,
  item,
  lang,
  currency,
}: {
  token: string;
  item: PublicShareItemSummary;
  lang: ShareLang;
  currency: DisplayCurrency;
}) {
  const display = pickDisplayPrice(item, currency);
  const dealLabel = dealTypeLabel(item.deal_type, lang);
  return (
    <div className="relative">
      <Link
        to={`/share/${token}/listings/${item.id}`}
        className="group block rounded-2xl overflow-hidden bg-white border border-[var(--mrd-line)] shadow-[0_2px_24px_rgba(18,32,54,.05)] hover:shadow-[0_12px_40px_rgba(18,32,54,.12)] transition-shadow transition-transform duration-300 hover:-translate-y-1.5"
      >
        <div className="relative aspect-[4/3] bg-[var(--mrd-bg)] overflow-hidden">
          {item.cover_photo_url ? (
            <img
              src={item.cover_photo_url}
              alt=""
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[var(--mrd-muted)] text-xs">
              {t("no_photo", lang)}
            </div>
          )}
          {dealLabel && (
            <span className="absolute top-3 left-3 rounded-full bg-white/85 backdrop-blur-md border border-[var(--mrd-line)] px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[.14em] text-[var(--mrd-navy)]">
              {dealLabel}
            </span>
          )}
        </div>
        <div className="p-5">
          {(item.neighborhood || item.district) && (
            <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[var(--mrd-muted)] truncate">
              {item.neighborhood || item.district}
            </div>
          )}
          <div className="mt-2 mshare-serif text-2xl font-medium text-[var(--mrd-navy)] leading-snug transition-colors group-hover:text-[var(--mrd-navy)]/70">
            {listingHeadline(item, lang)}
          </div>
          <div className="mt-4 flex items-center justify-between gap-2 pt-4 border-t border-[var(--mrd-line)] text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-[var(--mrd-navy)] tabular-nums whitespace-nowrap">
                {formatPrice(display.value, display.currency) || "—"}
              </span>
              {item.price_position && (
                <BenchmarkBadge
                  position={item.price_position}
                  percent={item.price_percent_vs_median ?? null}
                  lang={lang}
                  size="sm"
                  className="flex-shrink-0"
                />
              )}
            </div>
            <span className="text-[var(--mrd-muted)] whitespace-nowrap flex-shrink-0">
              {[
                item.rooms != null ? t("rooms_with_count", lang, { count: item.rooms }) : null,
                item.area_sqm ? `${Math.round(Number(item.area_sqm))} m²` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
        </div>
      </Link>
      {item.source_url && (
        <SourceLinkBadge url={item.source_url} className="absolute top-3 right-3 z-10" />
      )}
    </div>
  );
}

// ─── error / loading shell ─────────────────────────────────────────────────

export function PublicShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mshare min-h-screen bg-[var(--mrd-bg)] text-[var(--mrd-ink)] flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-2xl bg-white border border-[var(--mrd-line)] shadow-[0_2px_40px_rgba(18,32,54,.08)] p-8 text-center">
        <div className="mshare-serif text-2xl font-semibold text-[var(--mrd-navy)] mb-2">{title}</div>
        <div className="text-sm text-[var(--mrd-muted)]">{children}</div>
      </div>
    </div>
  );
}

// ─── reusable agent contact card (used by the detail page) ─────────────────

export function AgentCard({
  agent,
  lang,
  className = "",
  variant = "default",
}: {
  agent: { full_name: string | null; email: string | null; phone: string | null; agency_name: string | null };
  lang: ShareLang;
  className?: string;
  variant?: "default" | "sticky";
}) {
  const sticky = variant === "sticky";
  return (
    <div
      className={`rounded-2xl bg-white border border-[var(--mrd-line)] shadow-[0_2px_24px_rgba(18,32,54,.05)] ${
        sticky ? "p-5" : "p-5 sm:p-6"
      } ${className}`}
    >
      <div className="flex items-center gap-4">
        <div
          className="flex items-center justify-center w-14 h-14 rounded-full mshare-serif text-2xl font-semibold"
          style={{ background: "var(--mrd-navy)", color: "var(--mrd-accent)" }}
        >
          M
        </div>
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[.18em] text-[var(--mrd-muted)]">
            {t("agent_card_label", lang)}
          </div>
          <div className="mshare-serif text-lg font-semibold text-[var(--mrd-navy)] leading-tight">
            Meridian Tbilisi
          </div>
          <div className="text-xs text-[var(--mrd-muted)]">{t("agent_card_available", lang)}</div>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2">
        {agent.phone && (
          <a
            href={`tel:${agent.phone}`}
            className="inline-flex items-center justify-center gap-2 rounded-[999px] bg-[var(--mrd-accent)] hover:bg-[var(--mrd-accent-hover)] text-[var(--mrd-ink)] text-sm font-semibold px-4 py-3 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
            </svg>
            {t("btn_call_with_phone", lang, { phone: agent.phone })}
          </a>
        )}
        {agent.phone && (
          <a
            href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(t("whatsapp_default_msg", lang))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-[999px] border border-[rgba(16,120,80,.3)] bg-[rgba(16,120,80,.08)] hover:bg-[rgba(16,120,80,.16)] text-[#0f7a50] text-sm font-semibold px-4 py-3 transition-colors"
          >
            <WhatsAppIcon className="w-3.5 h-3.5" />
            {t("btn_whatsapp", lang)}
          </a>
        )}
        {agent.email && (
          <a
            href={`mailto:${agent.email}`}
            className="inline-flex items-center justify-center gap-2 rounded-[999px] border border-[var(--mrd-line)] bg-white hover:bg-[var(--mrd-navy)]/5 text-[var(--mrd-navy)] text-sm font-medium px-4 py-3 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-10 6L2 7" />
            </svg>
            <span className="truncate">{agent.email}</span>
          </a>
        )}
      </div>
    </div>
  );
}

// ─── shared helpers ────────────────────────────────────────────────────────

/** Cheap / average / expensive deal-quality chip. The data is the same
 *  the broker MatchCard renders — frozen into the share item's snapshot
 *  at link-creation time so the public page never re-queries benchmarks.
 *  Renders nothing when the listing has no benchmark (placements always,
 *  hot offers when their cohort had no rows). */
export function BenchmarkBadge({
  position,
  percent,
  median,
  sample,
  lang,
  size = "md",
  className = "",
}: {
  position?: "cheap" | "average" | "expensive" | null;
  percent?: number | null;
  median?: number | null;
  sample?: number | null;
  lang: ShareLang;
  size?: "sm" | "md";
  className?: string;
}) {
  if (!position) return null;
  const headline =
    position === "cheap"
      ? t("bench_cheap", lang)
      : position === "expensive"
        ? t("bench_expensive", lang)
        : t("bench_average", lang);
  const detail =
    position === "average" || percent == null || Math.abs(percent) < 1
      ? t("bench_at_median", lang)
      : percent < 0
        ? t("bench_pct_below", lang, { percent: Math.abs(percent).toFixed(0) })
        : t("bench_pct_above", lang, { percent: Math.abs(percent).toFixed(0) });
  // ``cheap`` is the strongest selling signal — paint it green, expensive
  // gets a soft warning, average a neutral slate.
  const palette =
    position === "cheap"
      ? "bg-[var(--mrd-accent)]/25 text-[var(--mrd-ink)] border-[var(--mrd-accent)]"
      : position === "expensive"
        ? "bg-rose-500/10 text-rose-600 border-rose-500/30"
        : "bg-[var(--mrd-line)] text-[var(--mrd-muted)] border-[var(--mrd-line)]";
  const ico =
    position === "cheap" ? "↓" : position === "expensive" ? "↑" : "≈";
  const tip =
    median != null
      ? t("bench_tooltip_neighborhood", lang, {
          value: Math.round(median).toLocaleString(),
        })
      : undefined;
  const sm = size === "sm";
  return (
    <span
      title={tip ? `${tip}${sample ? ` (n=${sample})` : ""}` : undefined}
      className={`inline-flex items-center gap-1.5 rounded-full border backdrop-blur-md font-semibold ${palette} ${
        sm ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"
      } ${className}`}
    >
      <span className="font-bold">{ico}</span>
      <span>{headline}</span>
      <span className="opacity-70 hidden sm:inline">· {detail}</span>
    </span>
  );
}

export function formatPrice(price: string | null, currency: string | null): string | null {
  if (!price) return null;
  const n = Number(price);
  if (!Number.isFinite(n)) return price;
  const symbol =
    currency === "USD" ? "$"
    : currency === "GEL" ? "₾"
    : currency === "EUR" ? "€"
    : currency ? `${currency} ` : "";
  return `${symbol}${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function WhatsAppIcon({ className = "" }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z" />
      <path d="M20.52 3.449C12.831-3.984.106 1.407.101 11.893c0 2.092.546 4.135 1.585 5.946L0 24l6.335-1.652a11.93 11.93 0 0 0 5.71 1.452h.005c9.846 0 16.067-10.692 11.165-19.13a11.821 11.821 0 0 0-2.696-3.221Zm1.273 18.06c-.815 1.414-2.034 2.46-3.451 2.876-1.417.416-2.88.416-4.297 0a11.93 11.93 0 0 1-5.71-1.452l-.408-.241-3.764.984.984-3.764-.241-.408a11.93 11.93 0 0 1-1.452-5.71c0-3.234 1.26-6.272 3.547-8.559C8.288 3.247 11.326 1.987 14.56 1.987c3.234 0 6.272 1.26 8.559 3.547 2.287 2.287 3.547 5.325 3.547 8.559a11.93 11.93 0 0 1-1.452 5.71l-3.421-.294Z" />
    </svg>
  );
}

// Re-exports for the detail-page sibling component.
export {
  BUILDING_STATUS_LABEL_BY_LANG,
  CONDITION_LABEL_BY_LANG,
  DEAL_TYPE_LABEL_BY_LANG,
  PROPERTY_TYPE_LABEL_BY_LANG,
  WhatsAppIcon,
};
